/**
 * Arama metinleri (WP 2.11). Kurallar `apps/web/src/i18n/tr.ts` ile aynı: cümle düzeni, fiil odaklı düğme, yanıltıcı başarı
 * iddiası yok. K-2.11-A: kısmi (tamamlanmamış) tarama hiçbir zaman "sonuç bulunamadı" diye sunulmaz; sayım kısmi yanıtta
 * yalnız gösterilen görünür sonuçlardır.
 */
import type { SearchRecordType, SearchSection } from '@castinflow/shared';
import type { SearchErrorKind } from './search-client';

export const searchStrings = {
  trigger: 'Ara',
  shortcut: 'Ctrl+K',
  // v3.9: sayfalar (“Sayfaya git”) ve kayıtlar birlikte aranır
  dialogTitle: 'Sayfa ve kayıt ara',
  inputLabel: 'Arama metni',
  placeholder: 'Sayfa adı, kişi, şirket, teklif, belge no veya #etiket',
  hint: 'En az 2 karakter yazın. Etiket için # ile başlayın.',
  loading: 'Aranıyor…',
  empty: 'Sonuç bulunamadı.',
  /** Kısmi tarama: ilk aday grubunda görünür sonuç yok ama arama bitmedi (kesin "sonuç yok" DEĞİL) */
  partialEmpty: 'İlk sonuç grubunda erişebileceğiniz kayıt bulunamadı.',
  continueSearch: 'Aramaya devam et',
  continueSearchIn: (label: string) => `${label} içinde aramaya devam et`,
  pendingNote: 'Yeni değişiklikler birkaç saniye içinde aramaya yansır.',
  buildingNote: 'Arama dizini hazırlanıyor; sonuçlar eksik olabilir.',
  scanNote:
    'Çok sayıda eşleşme var; kayıtlar sınırlı gruplar hâlinde taranıyor. Aramayı daraltabilir veya devam edebilirsiniz.',
  partialNote: 'Arama tamamlanmadı; daha fazla kayıt olabilir.',
  seeAll: (q: string) => `“${q}” için tüm sonuçları gör`,
  results: (n: number) => (n === 0 ? 'Sonuç yok' : `${String(n)} sonuç gösteriliyor`),
  resultsPartial: (n: number) =>
    `${String(n)} sonuç gösteriliyor; arama tamamlanmadı, devam edebilirsiniz.`,
  typeLabel: {
    contact: 'Kişiler',
    company: 'Şirketler',
    product: 'Ürünler',
    lead: 'Müşteri adayları',
    opportunity: 'Fırsatlar',
    proposal: 'Teklifler',
    order: 'Siparişler',
    purchase_order: 'Tedarikçi siparişleri',
    contract: 'Sözleşmeler',
    activity: 'Aktiviteler',
    ticket: 'Destek talepleri',
    form_document: 'Form belgeleri',
  } as const satisfies Record<SearchRecordType, string>,
  typeSingular: {
    contact: 'Kişi',
    company: 'Şirket',
    product: 'Ürün',
    lead: 'Müşteri adayı',
    opportunity: 'Fırsat',
    proposal: 'Teklif',
    order: 'Sipariş',
    purchase_order: 'Tedarikçi siparişi',
    contract: 'Sözleşme',
    activity: 'Aktivite',
    ticket: 'Destek talebi',
    form_document: 'Form belgesi',
  } as const satisfies Record<SearchRecordType, string>,
  count: (value: number, exact: boolean) =>
    exact ? `${String(value)} sonuç` : `${String(value)}+ sonuç`,
  shown: (value: number) => `${String(value)} gösteriliyor`,
  /** Bölüm sayım etiketi: kısmi taramada kesin toplam gibi sunulmaz */
  sectionCount: (sec: Pick<SearchSection, 'count' | 'partial'>, shown?: number) =>
    sec.partial
      ? `${String(shown ?? sec.count.value)} gösteriliyor`
      : sec.count.exact
        ? `${String(sec.count.value)} sonuç`
        : `${String(sec.count.value)}+ sonuç`,
  tagMatches: (names: string) => `Etiket: ${names}`,
  noTagMatch: 'Bu adla başlayan etiket yok.',
  error: {
    timeout: 'Arama çok uzun sürdü. Daha belirgin bir ifade yazın veya bir tür seçin.',
    rate: 'Kısa sürede çok fazla arama yapıldı. Birkaç saniye sonra yeniden deneyin.',
    invalid: 'Arama metni geçersiz. En az 2 karakterlik bir kelime yazın (en çok 5 kelime).',
    other: 'Arama yapılamadı.',
  } satisfies Record<SearchErrorKind, string>,
  retry: 'Yeniden dene',
  // TI-013: önizleme ve hızlı erişim
  preview: {
    title: 'Hızlı önizleme',
    back: 'Sonuçlara dön',
    open: 'Kayda git',
    show: (title: string) => `${title} önizlemesini aç`,
    showShort: 'Önizle',
    keyHint: 'Seçili sonucun önizlemesini açmak için sağ ok tuşuna basın.',
    failed: 'Önizleme yüklenemedi; kayda giderek açabilirsiniz.',
    recordNo: 'Kayıt no',
    owner: 'Sorumlu',
    contactKinds: {
      phone: 'Telefon',
      email: 'E-posta',
      address: 'Adres',
      web: 'Web',
      social: 'Sosyal medya',
    } as Readonly<Record<string, string>>,
    hint: 'Ok tuşlarıyla bir sonuç seçin; önizlemesi burada görünür. Giriş tuşu kaydı açar.',
  },
  pinned: {
    title: 'Hızlı erişim',
    note: 'Menüde yıldızla sabitlediğiniz sayfalar (yalnız bu tarayıcıda saklanır).',
    empty: 'Sık kullandığınız sayfaları menüdeki yıldızla sabitleyin; burada görünür.',
  },
  page: {
    title: 'Arama sonuçları',
    formLabel: 'Arama',
    submit: 'Ara',
    all: 'Hepsi',
    filters: 'Sonuç türü',
    tag: 'Etiket',
    anyTag: 'Tüm etiketler',
    showType: (label: string) => `Tüm ${label.toLocaleLowerCase('tr-TR')}`,
    more: 'Daha fazla göster',
    export: 'Dışa aktar',
    continueScan: 'Aramaya devam et',
    start: 'Aramak istediğiniz metni yazın.',
    typeSelect: 'Sonuç türü',
    allTypes: (n: string) => `Tüm kayıtlar (${n})`,
    typeOption: (label: string, n: string) => `${label} (${n})`,
    noResultTypes: 'Sonuç olmayan türler',
    resultsHeading: 'Sonuçlar',
  },
} as const;
